import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const fmt  = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const fmtP = e => `${MONTHS[(e.month || 1) - 1]} ${e.year}`

export default function Accrual({ projectId }) {
  const { can } = useProjectAuth(projectId)
  const [costCodes,    setCostCodes]    = useState([])
  const [accruals,     setAccruals]     = useState([])
  const [systemAccruals, setSystemAccruals] = useState([])
  const [showModal,    setShowModal]    = useState(false)
  const [editItem,     setEditItem]     = useState(null)
  const [form,         setForm]         = useState({
    sub_code_id: '', description: '', amount: '',
    month: new Date().getMonth() + 1, year: new Date().getFullYear(), notes: '',
    actual_cost_date: '2026-04-30'
  })

  // Package-based Accrual
  const [packages,     setPackages]     = useState([])
  const [bulkDate,     setBulkDate]     = useState('')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [selectedRows, setSelectedRows] = useState(new Set())
  const toggleRow = (id) => setSelectedRows(prev => { const n = new Set(prev); n.has(id)?n.delete(id):n.add(id); return n })
  const [selPkgs,      setSelPkgs]      = useState([])
  const [pkgData,      setPkgData]      = useState({})
  const [pkgLoading,   setPkgLoading]   = useState({})
  const [actDate,      setActDate]      = useState({})
  const [accrualAmt,   setAccrualAmt]   = useState({})
  const [showPkgPicker, setShowPkgPicker] = useState(false)

  const [subCodesList, setSubCodesList] = useState([]) // level=2 only, each carries Cost Code + Resource Code ancestry

  useEffect(() => {
    if (!projectId) return
    fetchCostCodes()
    fetchAccruals()
    fetchSystemAccruals()
  }, [projectId])

  // Fetch the whole hierarchy, then build the Sub Codes list (level 2) with its
  // ancestry resolved: parent (could be Cost Code level=1 OR Resource Code level=4),
  // and if the parent is a Resource Code, also resolve its own parent Cost Code.
  async function fetchCostCodes() {
    const { data } = await supabase.from('cost_codes')
      .select('id, code, description, level, parent_id').eq('project_id', projectId).eq('is_active', true).order('code')
    const all = data || []
    setCostCodes(all)
    const byId = {}
    all.forEach(c => { byId[c.id] = c })

    const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id] // could be Cost Code (level 1) or Resource Code (level 4)
      let costCode = null, resourceCode = null
      if (parent && parseInt(parent.level) === 4) {
        resourceCode = parent
        costCode = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        costCode = parent
      }
      return {
        ...sub,
        costCode: costCode?.code || '—',
        costCodeDescription: costCode?.description || '',
        resourceCode: resourceCode?.code || null,
        resourceCodeDescription: resourceCode?.description || null,
      }
    })
    setSubCodesList(subs)
  }

  async function applyBulkDate() {
    const ids = selectedRows.size > 0 ? [...selectedRows] : []
    if (!bulkDate) return alert('Select a date first')
    if (ids.length === 0) return alert('Select rows first')
    if (!window.confirm('Update ' + ids.length + ' records to ' + bulkDate + '?')) return
    setBulkUpdating(true)
    let hasError = false
    for (let i = 0; i < ids.length; i += 100) {
      const res = await supabase.from('accrual_entries').update({ actual_cost_date: bulkDate }).in('id', ids.slice(i, i+100))
      if (res.error) { hasError = true; alert('Error: '+res.error.message); break }
    }
    if (hasError) { setBulkUpdating(false); return }
    fetchAccruals(projectId); setSelectedRows(new Set())
    setBulkUpdating(false)
  }

  async function fetchAccruals() {
    const { data } = await supabase.from('accrual_entries')
      .select('*, cost_codes:sub_code_id(code, description, parent_id)')
      .eq('project_id', projectId)
      .order('year', { ascending: false }).order('month', { ascending: false })
    setAccruals(data || [])
  }

  async function fetchSystemAccruals() {
    const [ipcs, payrolls, equip] = await Promise.all([
      supabase.from('ipc_certificates')
        .select('certified_amount, subcontracts(cost_code_id, cost_codes(code))')
        .eq('project_id', projectId).in('status', ['submitted', 'under_review']),
      supabase.from('payroll_entries')
        .select('total_salary, cost_codes(code)')
        .eq('project_id', projectId).eq('status', 'draft'),
      supabase.from('equipment_costs')
        .select('invoiced_amount, cost_codes(code)')
        .eq('project_id', projectId).eq('status', 'draft'),
    ])
    const results = []
    for (const r of (ipcs.data   || [])) results.push({ source: 'IPC (Pending)',     code: r.subcontracts?.cost_codes?.code || '—', amount: r.certified_amount })
    for (const r of (payrolls.data || [])) results.push({ source: 'Payroll (Draft)', code: r.cost_codes?.code || '—',              amount: r.total_salary })
    for (const r of (equip.data  || [])) results.push({ source: 'Equipment (Draft)', code: r.cost_codes?.code || '—',              amount: r.invoiced_amount })
    setSystemAccruals(results)
  }

  function openNew() {
    setForm({ sub_code_id: '', description: '', amount: '',
      month: new Date().getMonth() + 1, year: new Date().getFullYear(), notes: '',
      actual_cost_date: '2026-04-30' })
    setEditItem(null); setShowModal(true)
  }

  async function saveAccrual() {
    if (!form.sub_code_id || !form.amount) { alert('Sub Code and Amount are required'); return }
    const payload = { project_id: projectId, sub_code_id: form.sub_code_id,
      description: form.description, amount: parseFloat(form.amount) || 0,
      month: parseInt(form.month), year: parseInt(form.year), notes: form.notes,
      actual_cost_date: form.actual_cost_date || '2026-04-30' }
    const { error } = editItem
      ? await supabase.from('accrual_entries').update(payload).eq('id', editItem.id)
      : await supabase.from('accrual_entries').insert(payload)
    if (error) { alert(error.message); return }
    setShowModal(false); fetchAccruals()
  }

  async function deleteAccrual(id) {
    if (!window.confirm('Delete this accrual?')) return
    await supabase.from('accrual_entries').delete().eq('id', id)
    fetchAccruals()
  }

  function exportAccruals() {
    const ws = XLSX.utils.json_to_sheet(accruals.map(a => {
      const sub = subCodesList.find(s => s.id === a.sub_code_id)
      return {
        'Cost Code': sub?.costCode || '—',
        'Resource Code': sub?.resourceCode || '—',
        'Resource Description': sub?.resourceCodeDescription || '',
        'Sub Code': a.cost_codes?.code || '—',
        'Description': a.description,
        'Month': MONTHS[(a.month||1)-1], 'Year': a.year,
        'Amount': a.amount, 'Notes': a.notes,
      }
    }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Accruals')
    XLSX.writeFile(wb, 'accruals.xlsx')
  }

  // Package-based Accrual
  async function loadPackages() {
    const { data } = await supabase.from('indirect_budget_items')
      .select('package_name').eq('project_id', projectId).not('package_name', 'is', null)
    const pkgs = [...new Set((data||[])
      .filter(r => r.package_name && r.package_name.trim().toLowerCase() !== 'total')
      .map(r => r.package_name.trim()))].sort()
    setPackages(pkgs)
  }

  async function loadPkgData(pkg) {
    setPkgLoading(prev => ({ ...prev, [pkg]: true }))
    const { data: rows } = await supabase.from('indirect_budget_items')
      .select('*, cost_codes(code,description)')
      .eq('project_id', projectId).eq('package_name', pkg)
      .order('sort_order', { ascending: true, nullsFirst: false })

    const items = (rows||[]).filter(r => (r.row_type||'').toLowerCase() !== 'total')
    const subCodes = [...new Set(items.map(r => r.sub_code).filter(Boolean))]

    const actualBySubCode = {}, avgBySubCode = {}
    if (subCodes.length) {
      const { data: ipcs } = await supabase.from('ipc_certificates').select('id')
        .eq('project_id', projectId).in('status', ['approved','paid'])
      if (ipcs?.length) {
        const { data: lines } = await supabase.from('ipc_line_items').select('sub_code,invoiced_amount')
          .in('ipc_id', ipcs.map(i=>i.id)).in('sub_code', subCodes)
        for (const l of (lines||[])) if (l.sub_code)
          actualBySubCode[l.sub_code] = (actualBySubCode[l.sub_code]||0) + (parseFloat(l.invoiced_amount)||0)
      }
      const { data: si } = await supabase.from('store_issues').select('sub_code,amount')
        .eq('project_id', projectId).in('sub_code', subCodes)
      for (const s of (si||[])) if (s.sub_code)
        actualBySubCode[s.sub_code] = (actualBySubCode[s.sub_code]||0) + (parseFloat(s.amount)||0)

      const { data: pos } = await supabase.from('purchase_orders')
        .select('sub_code,total_exc_vat,issue_date').eq('project_id', projectId)
        .in('status',['approved','paid','received','closed']).in('sub_code', subCodes)
      const grouped = {}
      for (const p of (pos||[])) {
        if (!p.sub_code) continue
        if (!grouped[p.sub_code]) grouped[p.sub_code] = { total: 0, dates: [] }
        grouped[p.sub_code].total += parseFloat(p.total_exc_vat)||0
        if (p.issue_date) grouped[p.sub_code].dates.push(new Date(p.issue_date))
      }
      for (const [sc, g] of Object.entries(grouped)) {
        let months = 1
        if (g.dates.length > 1) {
          const mn = new Date(Math.min(...g.dates.map(d=>d.getTime())))
          const mx = new Date(Math.max(...g.dates.map(d=>d.getTime())))
          months = Math.max(1, (mx.getFullYear()-mn.getFullYear())*12 + (mx.getMonth()-mn.getMonth()) + 1)
        }
        avgBySubCode[sc] = g.total / months
      }
    }

    setPkgData(prev => ({ ...prev, [pkg]: { items: items.map(row => ({
      ...row, actual: actualBySubCode[row.sub_code]||0, avgMonthly: avgBySubCode[row.sub_code]||0
    })) }}))
    setPkgLoading(prev => ({ ...prev, [pkg]: false }))
  }

  if (!projectId) return null

  return (
    <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>
      {/* Manual Accruals */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-[#1C1A18]">Manual Accrual</h2>
            <p className="text-xs text-[#9CA3AF] mt-0.5">تكاليف حصلت فعلاً بس مش مسجلة بعد</p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportAccruals} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">↓ Export</button>
            {can('Accrual.Create') && (
              <button onClick={openNew} className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">+ Manual Accrual</button>
            )}
          </div>
        </div>

        {accruals.length === 0 ? (
          <div className="text-center py-10 bg-[#EDE7DA] rounded-xl border border-[#E5E7EB]">
            <p className="text-4xl mb-2">📋</p>
            <p className="text-[#6B7280] text-sm">No manual accruals. Click "+ Manual Accrual".</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            {/* Bulk update bar */}
            <div className="flex items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-100 flex-wrap">
              <span className="text-xs font-semibold text-amber-700">
                {selectedRows.size > 0 ? `${selectedRows.size} selected` : 'Select rows to update date'}
              </span>
              <input type="date" value={bulkDate} onChange={e=>setBulkDate(e.target.value)}
                className="border border-amber-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-amber-500"/>
              <button onClick={applyBulkDate} disabled={!bulkDate||bulkUpdating||selectedRows.size===0}
                className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded hover:bg-amber-600 disabled:opacity-40">
                {bulkUpdating ? 'Updating...' : selectedRows.size > 0 ? `Apply to ${selectedRows.size} rows` : 'Select rows first'}
              </button>
              {selectedRows.size > 0 && <button onClick={()=>setSelectedRows(new Set())} className="text-xs text-amber-600 hover:underline bg-transparent border-0 cursor-pointer">Clear</button>}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="w-10 px-4 py-3"><input type="checkbox" checked={selectedRows.size===accruals.length&&accruals.length>0} onChange={()=>setSelectedRows(prev=>prev.size===accruals.length?new Set():new Set(accruals.map(a=>a.id)))} className="w-4 h-4"/></th>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Cost Code</th>
                  <th className="text-left px-4 py-3 font-medium text-purple-700">Resource Code</th>
                  <th className="text-left px-4 py-3 font-medium text-purple-700">Resource Description</th>
                  <th className="text-left px-4 py-3 font-medium text-[#1A7A4A]">Sub Code</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Description</th>
                  <th className="text-center px-4 py-3 font-medium text-[#6B7280]">Period</th>
                  <th className="text-right px-4 py-3 font-medium text-amber-700">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Actual Date</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Notes</th>
                  <th className="px-4 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F1EA]">
                {accruals.map(a => {
                  const sub = subCodesList.find(s => s.id === a.sub_code_id)
                  return (
                  <tr key={a.id} className="hover:bg-[#FAFAFA]">
                    <td className="px-4 py-3 text-center"><input type="checkbox" checked={selectedRows.has(a.id)} onChange={()=>toggleRow(a.id)} className="w-4 h-4"/></td>
                    <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{sub?.costCode || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-purple-700">{sub?.resourceCode || '—'}</td>
                    <td className="px-4 py-3 text-xs text-purple-700">{sub?.resourceCodeDescription || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A] font-semibold">{a.cost_codes?.code || '—'}</td>
                    <td className="px-4 py-3 text-[#2E2C29]">{a.description}</td>
                    <td className="px-4 py-3 text-center text-[#6B7280] text-xs">{fmtP(a)}</td>
                    <td className="px-4 py-3 text-right font-bold text-amber-600">{fmt(a.amount)}</td>
                    <td className="px-4 py-3 text-[#9CA3AF] text-xs">{a.actual_cost_date||'—'}</td>
                    <td className="px-4 py-3 text-[#9CA3AF] text-xs">{a.notes}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {can('Accrual.Edit') && (
                          <button onClick={() => { setForm({ sub_code_id: a.sub_code_id, description: a.description, amount: a.amount, month: a.month, year: a.year, notes: a.notes, actual_cost_date: a.actual_cost_date || '2026-04-30' }); setEditItem(a); setShowModal(true) }}
                            className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                        )}
                        {can('Accrual.Delete') && (
                          <button onClick={() => deleteAccrual(a.id)} className="text-[#9CA3AF] hover:text-red-500">🗑</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
              <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                <tr>
                  <td colSpan={3} className="px-4 py-3 font-medium text-[#6B7280]">Total Manual Accrual</td>
                  <td className="px-4 py-3 text-right font-bold text-amber-600">
                    SAR {fmt(accruals.reduce((s, a) => s + (parseFloat(a.amount)||0), 0))}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* System Accruals */}
      <div>
        <h2 className="text-lg font-bold text-[#1C1A18] mb-1">System Accruals</h2>
        <p className="text-xs text-[#9CA3AF] mb-3">IPC Pending + Payroll Draft + Equipment Draft</p>
        {systemAccruals.length === 0 ? (
          <div className="text-center py-8 bg-[#EDE7DA] rounded-xl border border-[#E5E7EB]">
            <p className="text-[#9CA3AF] text-sm">No system accruals.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6B7280]">Cost Code</th>
                  <th className="text-right px-4 py-3 font-medium text-[#1A7A4A]">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F1EA]">
                {systemAccruals.map((a, i) => (
                  <tr key={i} className="hover:bg-[#FAFAFA]">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.source.includes('IPC')     ? 'bg-green-100 text-green-700'  :
                        a.source.includes('Payroll') ? 'bg-blue-100 text-blue-700'    :
                                                       'bg-orange-100 text-orange-700'}`}>
                        {a.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{a.code}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmt(a.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                <tr>
                  <td colSpan={2} className="px-4 py-3 font-medium text-[#6B7280]">Total System Accrual</td>
                  <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">
                    SAR {fmt(systemAccruals.reduce((s,a)=>s+(parseFloat(a.amount)||0),0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Package-based Accrual */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-[#1C1A18]">Package Accrual</h2>
            <p className="text-xs text-[#9CA3AF] mt-0.5">حدد accrual لكل sub code في الـ package</p>
          </div>
          <button onClick={() => { loadPackages(); setShowPkgPicker(true) }}
            className="px-3 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 font-medium">
            + Add Package
          </button>
        </div>

        {showPkgPicker && (
          <div className="bg-white border border-violet-200 rounded-xl p-4 mb-4 shadow-sm">
            <p className="text-xs font-semibold text-violet-700 mb-2 uppercase tracking-wide">Select Package</p>
            <div className="flex flex-wrap gap-2">
              {packages.filter(p => !selPkgs.includes(p)).map(pkg => (
                <button key={pkg} onClick={() => { setSelPkgs(prev=>[...prev,pkg]); setShowPkgPicker(false); loadPkgData(pkg) }}
                  className="px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-700 text-xs rounded-lg hover:bg-violet-100">
                  {pkg}
                </button>
              ))}
              {packages.filter(p => !selPkgs.includes(p)).length === 0 && (
                <p className="text-xs text-[#9CA3AF]">All packages added</p>
              )}
            </div>
            <button onClick={() => setShowPkgPicker(false)} className="mt-2 text-xs text-[#9CA3AF] hover:text-[#6B7280]">Cancel</button>
          </div>
        )}

        {selPkgs.map(pkg => {
          const d = pkgData[pkg] || {}
          const loading = pkgLoading[pkg]
          const items = d.items || []
          const totAccrual = items.reduce((s,r) => s + (parseFloat(accrualAmt[r.id])||0), 0)

          return (
            <div key={pkg} className="mb-4 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-violet-600 text-white">
                <span className="font-semibold text-sm">📦 {pkg}</span>
                <div className="flex gap-2">
                  <button onClick={() => loadPkgData(pkg)} className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded">↻ Refresh</button>
                  <button onClick={() => setSelPkgs(prev=>prev.filter(p=>p!==pkg))} className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded">✕ Remove</button>
                </div>
              </div>
              {loading ? (
                <div className="py-8 text-center text-[#9CA3AF] text-sm">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F1EA] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[#6B7280] text-xs font-semibold uppercase">Description</th>
                      <th className="text-left px-3 py-2 text-[#6B7280] text-xs font-semibold uppercase">Sub Code</th>
                      <th className="text-right px-3 py-2 text-emerald-600 text-xs font-semibold uppercase">Actual</th>
                      <th className="text-center px-3 py-2 text-[#6B7280] text-xs font-semibold uppercase">Actual Date</th>
                      <th className="text-right px-3 py-2 text-blue-600 text-xs font-semibold uppercase">Avg/Month</th>
                      <th className="text-right px-3 py-2 text-violet-600 text-xs font-semibold uppercase bg-violet-50">Accrual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F5F1EA]">
                    {items.map(row => (
                      <tr key={row.id} className="hover:bg-[#FAFAFA]">
                        <td className="px-3 py-2 text-[#2E2C29] text-sm">{row.description || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-[#9CA3AF]">{row.sub_code || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-600">
                          {row.actual > 0 ? fmt(row.actual) : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="date" value={actDate[row.id]||''}
                            onChange={e => setActDate(prev => ({...prev,[row.id]:e.target.value}))}
                            className="text-xs border border-[#E5E7EB] rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-400 w-32"/>
                        </td>
                        <td className="px-3 py-2 text-right text-blue-600 text-xs">
                          {row.avgMonthly > 0 ? fmt(row.avgMonthly) : '—'}
                        </td>
                        <td className="px-3 py-2 bg-violet-50">
                          <input type="number" placeholder="0" min="0" value={accrualAmt[row.id]||''}
                            onChange={e => setAccrualAmt(prev => ({...prev,[row.id]:e.target.value}))}
                            className="w-full text-right text-xs font-bold border border-violet-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 text-violet-700"/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                    <tr>
                      <td colSpan={4} className="px-3 py-2 font-bold text-[#2E2C29] text-xs uppercase">Total Accrual — {pkg}</td>
                      <td></td>
                      <td className="px-3 py-2 text-right font-bold text-violet-700 bg-violet-50 text-sm">{fmt(totAccrual)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold text-[#1C1A18] mb-4">
              {editItem ? 'Edit Accrual' : 'New Manual Accrual'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Sub Code *</label>
                <select value={form.sub_code_id} onChange={e => setForm(f=>({...f,sub_code_id:e.target.value}))}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">— Select Sub Code —</option>
                  {subCodesList.map(s => <option key={s.id} value={s.id}>{s.code} — {s.description}</option>)}
                </select>
                {/* Cost Code + Resource Code are derived automatically from the selected Sub Code — never entered manually */}
                {form.sub_code_id && (() => {
                  const sel = subCodesList.find(s => s.id === form.sub_code_id)
                  if (!sel) return null
                  return (
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-[#6B7280]">
                        Cost Code: <span className="font-mono font-semibold text-[#1A7A4A]">{sel.costCode}</span> — {sel.costCodeDescription}
                      </p>
                      {sel.resourceCode && (
                        <p className="text-xs text-purple-700">
                          Resource Code: <span className="font-mono font-semibold">{sel.resourceCode}</span> — {sel.resourceCodeDescription}
                        </p>
                      )}
                    </div>
                  )
                })()}
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Description</label>
                <input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Amount *</label>
                  <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Month</label>
                  <select value={form.month} onChange={e=>setForm(f=>({...f,month:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Year</label>
                  <input type="number" value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Notes</label>
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Actual Cost Date</label>
                <input type="date" value={form.actual_cost_date||'2026-04-30'}
                  onChange={e=>setForm(f=>({...f,actual_cost_date:e.target.value}))}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={saveAccrual}
                className="px-5 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">
                {editItem ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
